import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ChefHat,
  Users,
  MapPin,
  MessageCircle,
  Star,
  Shield,
  Heart,
  ArrowRight,
  Check,
  Utensils,
  Home,
  Clock,
  Coins,
  CircleDollarSign,
  FileText,
  Map,
  ChevronDown,
  Lock,
  ScanFace,
} from "lucide-react";
import {
  SiInstagram,
  SiFacebook,
  SiThreads,
  SiX,
  SiTiktok,
} from "@icons-pack/react-simple-icons";

const API_URL =
  import.meta.env.VITE_API_URL ||
  "https://rango-api.gustavo-anacleto.workers.dev";

// FAQ Item Component
function FaqItem({
  icon,
  question,
  answer,
}: {
  icon: React.ReactNode;
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-orange-100 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-4 p-6 text-left hover:bg-orange-50/50 transition-colors"
      >
        <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 flex-shrink-0">
          {icon}
        </div>
        <span className="flex-1 font-semibold text-gray-900">{question}</span>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {isOpen && (
        <div className="px-6 pb-6 pt-0">
          <div className="pl-14 text-gray-600 leading-relaxed">{answer}</div>
        </div>
      )}
    </div>
  );
}

export function LandingPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [waitlistCount, setWaitlistCount] = useState<number | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const scrollToWaitlistAndFocus = () => {
    const waitlistSection = document.getElementById("waitlist");
    if (waitlistSection) {
      waitlistSection.scrollIntoView({ behavior: "smooth" });
      // Focus after scroll animation completes
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 500);
    }
  };

  // Fetch waitlist count on mount
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/v1/waitlist/count`);
        if (res.ok) {
          const data = await res.json();
          setWaitlistCount(data.count);
        }
      } catch (err) {
        console.error("Failed to fetch waitlist count:", err);
      }
    };
    fetchCount();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_URL}/api/v1/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error?.message || "Erro ao entrar na lista");
        return;
      }

      setSubmitted(true);
      // Increment local count
      if (waitlistCount !== null && !data.alreadyRegistered) {
        setWaitlistCount(waitlistCount + 1);
      }
    } catch (err) {
      console.error("Waitlist signup error:", err);
      setError("Erro de conexão. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // Format count display
  const formatWaitlistCount = () => {
    if (waitlistCount === null) return null;
    if (waitlistCount === 0) return null;
    if (waitlistCount < 100) {
      return `${waitlistCount} ${
        waitlistCount === 1 ? "pessoa já na fila" : "pessoas já na fila"
      }`;
    }
    const rounded = Math.floor(waitlistCount / 50) * 50;
    return `${rounded}+ pessoas já na fila`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-amber-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
                <Utensils className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-display font-bold text-orange-600">
                Rango
              </span>
            </div>
            <div className="hidden md:flex items-center gap-5 lg:gap-8 text-sm lg:text-base">
              <a
                href="#como-funciona"
                className="text-gray-600 hover:text-orange-600 transition-colors"
              >
                Como Funciona
              </a>
              <a
                href="#beneficios"
                className="text-gray-600 hover:text-orange-600 transition-colors"
              >
                Benefícios
              </a>
              <a
                href="#faq"
                className="text-gray-600 hover:text-orange-600 transition-colors"
              >
                Perguntas Frequentes
              </a>
              <a
                href="#waitlist"
                className="text-gray-600 hover:text-orange-600 transition-colors"
              >
                Lista de Espera
              </a>
            </div>
            <Button
              variant="outline"
              className="border-orange-300 text-orange-600 hover:bg-orange-50"
            >
              Em breve
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium">
                <Heart className="w-4 h-4" />
                Comida caseira, conexões reais
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-gray-900 leading-tight">
                Conectando quem quer{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
                  comer
                </span>{" "}
                com quem quer{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
                  cozinhar
                </span>
              </h1>

              <p className="text-xl text-gray-600 leading-relaxed max-w-xl">
                Reunimos quem ama cozinhar com quem quer fugir da rotina. Comida
                caseira acessível, novos amigos e experiências únicas na sua
                vizinhança.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg shadow-orange-500/30 h-14 px-8 text-lg"
                  onClick={scrollToWaitlistAndFocus}
                >
                  Entrar na lista de espera
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 text-lg border-gray-300"
                  onClick={() =>
                    document
                      .getElementById("como-funciona")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  Como funciona?
                </Button>
              </div>

              {formatWaitlistCount() && (
                <div className="flex items-center gap-6 pt-4">
                  <div className="flex -space-x-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-200 to-amber-200 border-2 border-white flex items-center justify-center text-orange-700 font-medium text-sm"
                      >
                        {["👨‍🍳", "👩‍🍳", "🧑‍🍳", "👨‍🍳"][i - 1]}
                      </div>
                    ))}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">
                      {formatWaitlistCount()}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="relative hidden lg:block">
              <div className="absolute -top-10 -right-10 w-72 h-72 bg-orange-200 rounded-full blur-3xl opacity-50" />
              <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-amber-200 rounded-full blur-3xl opacity-50" />

              <div className="relative bg-white rounded-3xl shadow-2xl shadow-orange-500/10 p-8 border border-orange-100">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 space-y-3">
                    <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                      <ChefHat className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900">
                      Quero Cozinhar
                    </h3>
                    <p className="text-sm text-gray-600">
                      Compartilhe seu talento e cubra seus custos
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 space-y-3">
                    <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                      <Utensils className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900">Quero Comer</h3>
                    <p className="text-sm text-gray-600">
                      Comida caseira sem preço de restaurante
                    </p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-lg">🍲</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        Feijoada da Maria
                      </p>
                      <p className="text-sm text-gray-500">
                        Sábado, 12h • 2km de você
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-orange-600">R$ 35</p>
                      <p className="text-xs text-gray-500">por pessoa</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              {
                icon: Utensils,
                value: "R$ 20-40",
                label: "Preço médio por refeição",
              },
              { icon: MapPin, value: "< 5km", label: "Distância média" },
              { icon: Users, value: "4-8", label: "Pessoas por mesa" },
              { icon: Star, value: "10%", label: "Taxa da plataforma" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 mx-auto bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <stat.icon className="w-6 h-6 text-orange-600" />
                </div>
                <p className="text-3xl font-display font-bold text-gray-900">
                  {stat.value}
                </p>
                <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="como-funciona" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
              Como funciona?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              É simples como compartilhar uma refeição com amigos
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-16">
            {/* For Hosts */}
            <div className="space-y-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center">
                  <ChefHat className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-display font-bold text-gray-900">
                  Para quem cozinha
                </h3>
              </div>

              <div className="space-y-6">
                {[
                  {
                    step: "1",
                    title: "Diga o que quer preparar",
                    desc: "Escolha o prato, defina quantas pessoas pode servir e o valor por pessoa.",
                    icon: Utensils,
                  },
                  {
                    step: "2",
                    title: "Receba interessados",
                    desc: "Pessoas próximas verão sua oferta e podem entrar em contato via chat.",
                    icon: MessageCircle,
                  },
                  {
                    step: "3",
                    title: "Cozinhe e compartilhe",
                    desc: "Receba seus convidados, sirva sua comida e cubra seus custos.",
                    icon: Heart,
                  },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* For Guests */}
            <div className="space-y-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-display font-bold text-gray-900">
                  Para quem quer comer
                </h3>
              </div>

              <div className="space-y-6">
                {[
                  {
                    step: "1",
                    title: "Busque na sua região",
                    desc: "Veja o que estão cozinhando perto de você, filtre por prato e preço.",
                    icon: MapPin,
                  },
                  {
                    step: "2",
                    title: "Converse e reserve",
                    desc: "Tire dúvidas pelo chat, confirme detalhes e reserve seu lugar pagando via PIX.",
                    icon: MessageCircle,
                  },
                  {
                    step: "3",
                    title: "Aproveite a experiência",
                    desc: "Compareça, conheça pessoas novas e saboreie comida caseira de verdade.",
                    icon: Home,
                  },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 font-bold">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section
        id="beneficios"
        className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-orange-50 to-white"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
              Por que usar o Rango?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Uma nova forma de se conectar através da comida
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Coins,
                title: "Economia real",
                desc: "Coma bem por uma fração do preço de restaurante. Sem taxa de entrega, sem intermediários.",
                color: "orange",
              },
              {
                icon: Users,
                title: "Conexões locais",
                desc: "Conheça seus vizinhos, faça novos amigos e fortaleça sua comunidade.",
                color: "amber",
              },
              {
                icon: Heart,
                title: "Comida de verdade",
                desc: "Pratos feitos com carinho, ingredientes frescos e receitas tradicionais.",
                color: "orange",
              },
              {
                icon: Shield,
                title: "Segurança garantida",
                desc: "Verificação de identidade, avaliações de usuários e pagamento protegido.",
                color: "amber",
              },
              {
                icon: Clock,
                title: "Flexibilidade total",
                desc: "Cozinhe quando quiser, coma quando puder. Você define sua disponibilidade.",
                color: "orange",
              },
              {
                icon: Star,
                title: "Ganhe recompensas",
                desc: "Acumule tokens a cada refeição e participe do sucesso da plataforma.",
                color: "amber",
              },
            ].map((benefit, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-8 shadow-lg shadow-orange-500/5 border border-orange-100 hover:shadow-xl hover:shadow-orange-500/10 transition-shadow"
              >
                <div
                  className={`w-14 h-14 bg-${benefit.color}-100 rounded-2xl flex items-center justify-center mb-6`}
                >
                  <benefit.icon
                    className={`w-7 h-7 text-${benefit.color}-600`}
                  />
                </div>
                <h3 className="text-xl font-display font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Driven */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-8">
            <Star className="w-4 h-4" />
            Plataforma transparente
          </div>

          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
            Uma empresa construída pela comunidade
          </h2>

          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            O Rango não é só uma plataforma — é um movimento. Somos
            transparentes sobre todas as finanças e compartilhamos o sucesso com
            quem usa o app. Cada refeição que você faz ou serve te dá tokens que
            representam participação real no futuro da empresa.
          </p>

          <div className="grid grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <p className="text-4xl font-display font-bold text-orange-600">
                49%
              </p>
              <p className="text-gray-600 mt-2">Para a comunidade</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-display font-bold text-orange-600">
                10%
              </p>
              <p className="text-gray-600 mt-2">Taxa por transação</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-display font-bold text-orange-600">
                100%
              </p>
              <p className="text-gray-600 mt-2">Transparência</p>
            </div>
          </div>
        </div>
      </section>

      {/* Resources & FAQ Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-orange-50">
        <div className="max-w-7xl mx-auto">
          {/* Resources Links */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="relative flex items-center gap-6 bg-gray-50 rounded-2xl p-8 border border-gray-200 opacity-60 cursor-not-allowed">
              <div className="absolute top-4 right-4">
                <span className="bg-gray-200 text-gray-500 text-xs font-medium px-3 py-1 rounded-full">
                  Em breve
                </span>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-gray-300 to-gray-400 rounded-2xl flex items-center justify-center flex-shrink-0">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-display font-bold text-gray-500 mb-1">
                  Whitepaper
                </h3>
                <p className="text-gray-400">
                  Conheça nossa visão, tokenomics e modelo de negócio em
                  detalhes.
                </p>
              </div>
            </div>

            <div className="relative flex items-center gap-6 bg-gray-50 rounded-2xl p-8 border border-gray-200 opacity-60 cursor-not-allowed">
              <div className="absolute top-4 right-4">
                <span className="bg-gray-200 text-gray-500 text-xs font-medium px-3 py-1 rounded-full">
                  Em breve
                </span>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-gray-400 to-gray-300 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Map className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-display font-bold text-gray-500 mb-1">
                  Roadmap
                </h3>
                <p className="text-gray-400">
                  Acompanhe nosso progresso e os próximos passos da plataforma.
                </p>
              </div>
            </div>
          </div>

          {/* Subscribe for updates button */}
          <div className="text-center mb-20">
            <p className="text-gray-600 mb-4">
              Se inscreva para receber atualizações sobre o lançamento da
              plataforma, whitepaper e roadmap.
            </p>
            <Button
              onClick={scrollToWaitlistAndFocus}
              size="lg"
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg shadow-orange-500/30 h-14 px-8 text-lg"
            >
              Entrar na lista de espera
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto pt-4" id="faq">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Segurança e Confiança
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900">
                Perguntas Frequentes
              </h2>
            </div>

            <div className="space-y-4">
              <FaqItem
                icon={<Lock className="w-5 h-5" />}
                question="Como o Rango protege meus dados pessoais?"
                answer="Utilizamos criptografia de ponta a ponta e seguimos rigorosamente a LGPD. Seus dados são armazenados de forma segura na infraestrutura da Cloudflare, com backups redundantes. Nunca compartilhamos suas informações pessoais com terceiros sem seu consentimento explícito."
              />
              <FaqItem
                icon={<ScanFace className="w-5 h-5" />}
                question="Por que a Verificação de Identidade (KYC) é necessária?"
                answer="Implementamos processos rigorosos de reconhecimento facial e validação de dados para criar um ambiente de confiança. Essa tecnologia de verificação de identidade garante que cada usuário é único, prevenindo comportamentos mal-intencionados e fraudes. Como a nossa comunidade se baseia em encontros presenciais, a biometria é a nossa maior aliada para proteger a sua casa e garantir uma convivência segura para todos."
              />
              <FaqItem
                icon={<Shield className="w-5 h-5" />}
                question="O que acontece se alguém tiver comportamento inadequado?"
                answer="Temos um sistema robusto de avaliações mútuas. Usuários com comportamento inadequado recebem advertências e podem ser banidos permanentemente. Graças à Verificação de Identidade, pessoas banidas não conseguem criar novas contas. Além disso, incentivamos denúncias e temos uma equipe dedicada para analisar casos graves."
              />
              <FaqItem
                icon={<Home className="w-5 h-5" />}
                question="É seguro receber estranhos na minha casa?"
                answer="No Rango, você sempre decide quem entra: todo convidado precisa ser aprovado antes da confirmação do encontro. Para ajudar você a decidir com mais segurança, mostramos avaliações de outros usuários e quantos encontros a pessoa já realizou na plataforma. Recomendamos que, se possível, os encontros sejam realizados em áreas que limitem o acesso ao restante da residência. Nosso código de conduta exige comportamento adequado de todos os usuários. Plataformas que intermediam encontros presenciais sempre envolvem algum risco — exercite cautela. Acreditamos que estreitar os laços com sua vizinhança torna a comunidade mais segura, forte e conectada."
              />
              <FaqItem
                icon={<CircleDollarSign className="w-5 h-5" />}
                question="Os pagamentos são seguros?"
                answer="Sim! Todos os pagamentos são processados via PIX com intermediação da plataforma. O anfitrião só recebe após a realização do encontro. Caso algo não ocorra como esperado, contamos com um processo de disputa claro e transparente."
              />
              <FaqItem
                icon={<Coins className="w-5 h-5" />}
                question="Como funcionam as recompensas?"
                answer="Estamos criando o RANGO Token, um token digital que representa uma participação econômica no ecossistema da plataforma. Sempre que você participa — cozinhando ou comendo — você recebe tokens como recompensa. Esses tokens permitem que usuários compartilhem do sucesso do Rango, recebendo parte dos resultados financeiros gerados pela plataforma de forma proporcional à sua participação. Diferente de programas tradicionais de pontos, o RANGO Token tem oferta limitada e emissão controlada, o que valoriza quem participa desde cedo. Ele pode ser acumulado, utilizado dentro do ecossistema e também comprado ou vendido em mercados compatíveis. Todo o sistema foi desenhado com foco em transparência, responsabilidade e sustentabilidade, garantindo que o crescimento da plataforma beneficie quem realmente a constrói no dia a dia. Se inscreva para saber mais quando nosso Whitepaper ficar disponível com todos os detalhes."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist Section */}
      <section id="waitlist" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-3xl p-8 md:p-12 text-white text-center shadow-2xl shadow-orange-500/30">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
              Seja um dos primeiros!
            </h2>
            <p className="text-lg text-orange-100 mb-8 max-w-xl mx-auto">
              Entre na lista de espera e ganhe acesso antecipado quando
              lançarmos. Os primeiros usuários receberão um bônus especial de
              tokens!
            </p>

            {submitted ? (
              <div className="bg-white/20 backdrop-blur rounded-2xl p-8">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Você está na lista!</h3>
                <p className="text-orange-100">
                  Te avisaremos assim que o Rango estiver disponível na sua
                  região.
                </p>
              </div>
            ) : (
              <div className="max-w-md mx-auto">
                <form
                  onSubmit={handleSubmit}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <Input
                    ref={emailInputRef}
                    type="email"
                    placeholder="Seu melhor e-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-14 bg-white/20 border-white/30 text-white placeholder:text-white/60 rounded-xl"
                    required
                  />
                  <Button
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="h-14 px-8 bg-white text-orange-600 hover:bg-orange-50 rounded-xl font-semibold whitespace-nowrap"
                  >
                    {loading ? "Entrando..." : "Entrar na fila"}
                  </Button>
                </form>
                {error && (
                  <p className="text-white/90 text-sm mt-3 bg-white/10 rounded-lg px-4 py-2">
                    {error}
                  </p>
                )}
              </div>
            )}

            <p className="text-sm text-orange-200 mt-6">
              Sem spam, prometemos. Apenas atualizações sobre o lançamento.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
                  <Utensils className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-display font-bold">Rango</span>
              </div>

              <div className="flex flex-wrap justify-center gap-6 md:gap-8 text-gray-400">
                <a href="#" className="hover:text-white transition-colors">
                  Sobre
                </a>
                <a href="#" className="hover:text-white transition-colors">
                  Privacidade
                </a>
                <a href="#" className="hover:text-white transition-colors">
                  Termos
                </a>
                <a
                  href="mailto:contato@rango.cloud"
                  className="hover:text-white transition-colors"
                >
                  Contato
                </a>
              </div>

              <div className="flex items-center gap-4">
                <a
                  href="https://instagram.com/vembaterumrango"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Instagram"
                >
                  <SiInstagram className="w-5 h-5" />
                </a>
                <a
                  href="https://threads.net/@vembaterumrango"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Threads"
                >
                  <SiThreads className="w-5 h-5" />
                </a>
                <a
                  href="https://x.com/VemBaterUmRango"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="X"
                >
                  <SiX className="w-5 h-5" />
                </a>
                <a
                  href="https://www.facebook.com/share/1GHzbXBG9J/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Facebook"
                >
                  <SiFacebook className="w-5 h-5" />
                </a>
                <a
                  href="https://www.tiktok.com/@vembaterumrango"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="TikTok"
                >
                  <SiTiktok className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div className="border-t border-gray-800 pt-8">
              <p className="text-gray-500 text-sm text-center">
                © 2025 Rango. Feito com ❤️ no Brasil.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
